import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { SETTLEMENT_STATUS_TO_COLOR, SETTLEMENTS_ENDPOINT } from "../../../../consts";
import { formatCounterValue, formatNumber } from "../../../../utils";
import axios from "../../../../api/axios";
import styles from "./saleSummaryDetail.module.css";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { EditNote, Handshake, Person4 } from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import { useHasPermission } from "../../../../contexts/auth";
import { SettlementActionModal } from "./settlementActionModal.component";

type SettlementPositionSummary = {
  position_id: number;
  counter_kind: string;
  last_counter_value: number | null;
  new_counter_value: number;
  counter_difference: number;
  price_per_unit: number;
  included_in_rent: number;
  quantity_over_included_in_rent: number | null;
};

type SettlementSummaryItem = {
  settlement_id: number;
  status: string;
  status_display: string;
  settlement_kind?: string | null;
  settlement_kind_display?: string | null;
  is_common_counter?: boolean;
  total_calculated: number;
  calculated_sum: number;
  extra_positions_total: number;
  inflation_rate_percent?: number | string | null;
  total_overridden: number | null;
  display_total: number;
  equipment_name: string;
  equipment_serial: string;
  rent_price: number;
  proportions_factor?: number | string | null;
  positions: SettlementPositionSummary[];
};

type SettlementTotalsSummary = {
  settlement_count: number;
  usage_calculated: number;
  extra_positions_total: number;
  total_calculated: number;
  total_overridden: number;
  display_total: number;
  pending_count: number;
};

type SaleDateContractSummary = {
  contract_number: string;
  contract_internal_name?: string | null;
  settlement_kind: string;
  settlements: SettlementSummaryItem[];
  totals: SettlementTotalsSummary;
};

type ClientOnSaleSummary = {
  id: number;
  name: string;
  short_name: string | null;
  nip: string | null;
  regon: string | null;
  krs: string | null;
  pesel: string | null;
};

type SaleSummaryResponse = {
  client: ClientOnSaleSummary;
  contracts: SaleDateContractSummary[];
};

type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

type SaleSummaryDetailProps = {
  saleDate?: string;
  contractId?: string;
  search?: string;
  showHeader?: boolean;
};

const formatInflationRatePercent = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return `${formatNumber(value)}`;
};

const hasInflationRate = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  return Number(value) !== 0;
};

export const SaleSummaryDetail = ({
  saleDate: saleDateProp,
  contractId: contractIdProp,
  search = "",
  showHeader = true,
}: SaleSummaryDetailProps) => {
  const SALE_SUMMARY_PAGE_SIZE = 5;
  const { id, saleDate: saleDateFromParams } = useParams();
  const saleDate = saleDateProp ?? saleDateFromParams;
  const contractId = contractIdProp ?? id;
  const { t } = useTranslation();

  const renderRentPrice = (settlement: SettlementSummaryItem) => (
    <>
      {formatNumber(settlement.rent_price)}
      {settlement.proportions_factor != null && settlement.proportions_factor !== "" ? (
        <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 0.25 }}>
          {t("(included proportions of: {{factor}})", {
            factor: formatNumber(settlement.proportions_factor),
          })}
        </Typography>
      ) : null}
    </>
  );

  const renderSettlementTotal = (settlement: SettlementSummaryItem) => {
    if (
      settlement.total_overridden != null &&
      settlement.total_overridden !== settlement.total_calculated
    ) {
      return (
        <>
          <Typography
            component="span"
            variant="body2"
            color="textSecondary"
            sx={{ textDecoration: "line-through", display: "block" }}
          >
            {formatNumber(settlement.total_calculated)}
          </Typography>
          <span className={styles.totalCell}>{formatNumber(settlement.total_overridden)}</span>
        </>
      );
    }

    return <span className={styles.totalCell}>{formatNumber(settlement.display_total)}</span>;
  };

  const renderPositionTable = (
    settlement: SettlementSummaryItem,
    showTitle: boolean = true
  ) => {
    if (!settlement.positions || settlement.positions.length === 0) {
      return null;
    }

    return (
      <TableContainer component={Paper} variant="outlined" className={styles.noScrollTable}>
        <Table size="small" className={styles.squeezeTable}>
          <TableHead>
            {showTitle ? (
              <TableRow>
                <TableCell colSpan={5} className={styles.positionTableTitleCell}>
                  <Typography variant="subtitle2">{settlement.equipment_name}</Typography>
                  {settlement.equipment_serial ? (
                    <Typography variant="body2" className={styles.equipmentCodeBold}>
                      {settlement.equipment_serial}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            ) : null}
            <TableRow className={styles.tableHeader}>
              <TableCell>{t("Counter")}</TableCell>
              <TableCell align="right">{t("Last value")}</TableCell>
              <TableCell align="right">{t("New value")}</TableCell>
              <TableCell align="right">{t("Price/Unit")}</TableCell>
              <TableCell align="right">{t("Included in rent")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settlement.positions.map((pos, posIdx) => {
              const exceedsLimit = (pos.quantity_over_included_in_rent ?? 0) > 0;

              return (
                <TableRow key={`${settlement.settlement_id}-${pos.position_id}-${posIdx}`}>
                  <TableCell>{pos.counter_kind}</TableCell>
                  <TableCell align="right">
                    {pos.last_counter_value !== null
                      ? formatCounterValue(pos.last_counter_value)
                      : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {formatCounterValue(pos.new_counter_value)}
                    <br />
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{
                        fontWeight: exceedsLimit ? 700 : 400,
                        color: exceedsLimit ? "text.primary" : "text.secondary",
                      }}
                    >
                      <i>+{formatCounterValue(pos.counter_difference)}</i>
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(pos.price_per_unit)}</TableCell>
                  <TableCell align="right">
                    {formatCounterValue(pos.included_in_rent)}
                    <br />
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{
                        fontWeight: exceedsLimit ? 700 : 400,
                        color: exceedsLimit ? "text.primary" : "text.secondary",
                      }}
                    >
                      <i>+{formatCounterValue(pos.quantity_over_included_in_rent ?? 0)}</i>
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCounterUsageSummary = (settlement: SettlementSummaryItem) => {
    const positions = settlement.positions || [];
    if (positions.length === 0) {
      return null;
    }

    const differenceByKind = new Map<string, number>();
    positions.forEach((pos) => {
      const current = differenceByKind.get(pos.counter_kind) || 0;
      differenceByKind.set(pos.counter_kind, current + Number(pos.counter_difference || 0));
    });

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
        <Typography variant="subtitle2">{t("Counter usage summary")}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {Array.from(differenceByKind.entries()).map(([kind, total]) => (
            <Typography key={kind} variant="body2">
              {kind}: <strong>+{formatCounterValue(total)}</strong>
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };

  const renderDetailItem = (label: string, value: React.ReactNode) => (
    <Box className={styles.detailItem}>
      <Typography
        variant="caption"
        color="textSecondary"
        component="div"
        className={styles.detailLabel}
      >
        {label}
      </Typography>
      <Box className={styles.detailValue}>{value}</Box>
    </Box>
  );

  const renderSettlementCard = (settlement: SettlementSummaryItem) => {
    const statusColor = SETTLEMENT_STATUS_TO_COLOR[settlement.status];
    const showExtraPositions = Number(settlement.extra_positions_total) !== 0;
    const showInflation = hasInflationRate(settlement.inflation_rate_percent);

    return (
      <Box
        key={settlement.settlement_id}
        className={styles.settlementCard}
        sx={{
          borderLeft: "4px solid",
          borderLeftColor: statusColor ? `${statusColor}.main` : "divider",
        }}
      >
        <Box className={styles.settlementHeader}>
          <Box>
            <Typography variant="subtitle2">{settlement.equipment_name}</Typography>
            {settlement.equipment_serial ? (
              <Typography variant="body2" className={styles.equipmentCodeBold}>
                {settlement.equipment_serial}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Chip label={settlement.status_display} color={statusColor || "default"} size="small" />
            {settlement.status !== "C" ? (
              <Tooltip title={t("Manage settlement")}>
                <IconButton
                  size="small"
                  onClick={() => setActionModalSettlementId(settlement.settlement_id)}
                >
                  <EditNote fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>

        <Box className={styles.detailGrid}>
          {renderDetailItem(t("Rent price"), renderRentPrice(settlement))}
          {renderDetailItem(t("Total for Usage"), formatNumber(settlement.calculated_sum))}
          {showExtraPositions
            ? renderDetailItem(
                t("Extra positions"),
                formatNumber(settlement.extra_positions_total)
              )
            : null}
          {showInflation
            ? renderDetailItem(
                t("Inflation"),
                formatInflationRatePercent(settlement.inflation_rate_percent)
              )
            : null}
          {renderDetailItem(t("Total"), renderSettlementTotal(settlement))}
        </Box>

        {settlement.positions && settlement.positions.length > 0 ? (
          <Box className={styles.positionsSection}>
            {renderPositionTable(settlement, false)}
            {settlement.is_common_counter ? renderCounterUsageSummary(settlement) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderContractSettlements = (contractSummary: SaleDateContractSummary) => {
    const showExtraPositionsTotal = Number(contractSummary.totals.extra_positions_total) !== 0;
    const hasCommonCounterSettlement = contractSummary.settlements.some(
      (settlement) => settlement.is_common_counter
    );

    return (
      <>
        <Box className={styles.contractHeader}>
          <Handshake fontSize="small" />
          <strong>{contractSummary.contract_number}</strong>
          {contractSummary.contract_internal_name ? (
            <Typography component="span" variant="body2" color="textSecondary">
              {contractSummary.contract_internal_name}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
          {contractSummary.settlements.map((settlement) => renderSettlementCard(settlement))}
        </Box>

        {hasCommonCounterSettlement ? (
          <Box className={styles.summaryRow}>
            <Typography variant="subtitle2">{t("Summary")}</Typography>
            <Box className={styles.detailGrid}>
              {renderDetailItem(
                t("Total for Usage"),
                formatNumber(contractSummary.totals.usage_calculated)
              )}
              {showExtraPositionsTotal
                ? renderDetailItem(
                    t("Extra positions"),
                    formatNumber(contractSummary.totals.extra_positions_total)
                  )
                : null}
              {renderDetailItem(
                t("Total"),
                <span className={styles.totalCell}>
                  {formatNumber(contractSummary.totals.display_total)}
                </span>
              )}
            </Box>
            {contractSummary.totals.pending_count > 0 ? (
              <Typography variant="caption" color="textSecondary">
                {t("{{count}} pending settlement(s) not included in this total", {
                  count: contractSummary.totals.pending_count,
                })}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </>
    );
  };

  const [summaryData, setSummaryData] = useState<SaleSummaryResponse[]>([]);
  const canViewSaleSummary = useHasPermission("settlement.view_sale_summary");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [contractNumberSort, setContractNumberSort] = useState<"asc" | "desc">(
    "desc"
  );
  const [settlementFilter, setSettlementFilter] = useState<
    "all" | "fully_settled" | "not_settled"
  >("all");
  const [actionModalSettlementId, setActionModalSettlementId] = useState<number | null>(
    null
  );
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  const fetchData = useCallback(async (targetPage = 1, append = false) => {
    setError(null);
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    if (!saleDate) {
      setSummaryData([]);
      setHasMore(false);
      setPage(1);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        sale_date: saleDate,
      });

      if (contractId) {
        queryParams.set("contract_id", contractId);
      }

      if (search) {
        queryParams.set("search", search);
      }

      queryParams.set("page", String(targetPage));
      queryParams.set("page_size", String(SALE_SUMMARY_PAGE_SIZE));
      queryParams.set("contract_number_sort", contractNumberSort);

      if (!contractId && settlementFilter === "fully_settled") {
        queryParams.set("fully_settled_only", "true");
      }

      if (!contractId && settlementFilter === "not_settled") {
        queryParams.set("not_settled_only", "true");
      }

      const response = await axios.get(`${SETTLEMENTS_ENDPOINT}sale-summary/?${queryParams.toString()}`);
      const payload = response.data as PaginatedResponse<SaleSummaryResponse> | SaleSummaryResponse[];
      const results = Array.isArray(payload) ? payload : payload?.results || [];
      const hasNextPage = Array.isArray(payload) ? false : Boolean(payload?.next);

      setSummaryData((prev) => (append ? [...prev, ...results] : results));
      setHasMore(hasNextPage);
      setPage(targetPage);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("Failed to load summary")
      );
      console.error("Error fetching summary:", err);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [contractId, contractNumberSort, settlementFilter, saleDate, search, t]);

  useEffect(() => {
    const handleSettlementRefresh = () => {
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener("settlementConfirmed", handleSettlementRefresh);
    window.addEventListener("settlementUpdated", handleSettlementRefresh);
    window.addEventListener("equipmentCounterAdded", handleSettlementRefresh);
    return () => {
      window.removeEventListener("settlementConfirmed", handleSettlementRefresh);
      window.removeEventListener("settlementUpdated", handleSettlementRefresh);
      window.removeEventListener("equipmentCounterAdded", handleSettlementRefresh);
    };
  }, []);

  useEffect(() => {
    if (!canViewSaleSummary || !saleDate) {
      return;
    }
    setSummaryData([]);
  }, [contractNumberSort, settlementFilter, canViewSaleSummary, saleDate]);

  useEffect(() => {
    if (!canViewSaleSummary) {
      setLoading(false);
      setSummaryData([]);
      setError(null);
      return;
    }
    fetchData(1, false);
  }, [
    canViewSaleSummary,
    saleDate,
    contractId,
    search,
    refreshKey,
    contractNumberSort,
    settlementFilter,
    fetchData,
  ]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) {
      return;
    }

    const trigger = loadMoreTriggerRef.current;
    if (!trigger) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting && !loading && !loadingMore && hasMore) {
          fetchData(page + 1, true);
        }
      },
      {
        root: null,
        rootMargin: "200px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(trigger);

    return () => {
      observer.disconnect();
    };
  }, [fetchData, hasMore, loading, loadingMore, page]);

  if (!canViewSaleSummary) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {t("You do not have permission to view the sale summary.")}
      </Alert>
    );
  }

  const sortToolbar = saleDate ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        width: "100%",
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={contractNumberSort}
        onChange={(_event, value: "asc" | "desc" | null) => {
          if (value !== null) {
            setContractNumberSort(value);
          }
        }}
        aria-label={t("Ordering") + ":"}
      >
        <ToggleButton value="desc">{t("Newest number first")}</ToggleButton>
        <ToggleButton value="asc">{t("Oldest number first")}</ToggleButton>
      </ToggleButtonGroup>

      {!contractId ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={settlementFilter}
          onChange={(
            _event,
            value: "all" | "fully_settled" | "not_settled" | null
          ) => {
            if (value !== null) {
              setSettlementFilter(value);
            }
          }}
          aria-label={t("Contracts") + ":"}
        >
          <ToggleButton value="all">{t("All contracts")}</ToggleButton>
          <ToggleButton value="fully_settled">{t("Fully settled only")}</ToggleButton>
          <ToggleButton value="not_settled">{t("Not settled only")}</ToggleButton>
        </ToggleButtonGroup>
      ) : null}
    </Box>
  ) : null;

  if (loading && summaryData.length === 0) {
    return (
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
        {sortToolbar}
        <CustomCircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
        {sortToolbar}
        <Box sx={{ p: 3, color: "error.main" }}>{error}</Box>
      </Box>
    );
  }

  if (summaryData.length === 0) {
    return (
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
        {sortToolbar}
        <Box sx={{ p: 3 }}>{t("No data available")}</Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      {sortToolbar ? (
        <Box sx={{ mb: 2, width: "100%" }}>{sortToolbar}</Box>
      ) : null}
      {showHeader && (
        <Typography variant="h6" className={styles.header}>
          {t("Sale summary")} ({saleDate})
        </Typography>
      )}

      {summaryData.map((clientSummary) => (
        <Box className={styles.section} key={clientSummary.client.id}>
          <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Person4 />
            {clientSummary.client.short_name || clientSummary.client.name}
            {clientSummary.client.nip ? (
              <Typography component="span" variant="body2" color="textSecondary">
                {t("NIP")}: {clientSummary.client.nip}
              </Typography>
            ) : null}
          </Typography>

          {clientSummary.contracts.map((contractSummary, contractIdx) => (
            <Box
              sx={{ mb: 2 }}
              key={`${clientSummary.client.id}-${contractSummary.contract_number}-${contractIdx}`}
            >
              {renderContractSettlements(contractSummary)}
            </Box>
          ))}
        </Box>
      ))}

      {loadingMore && (
        <Box className={styles.loadMoreLoader}>
          <CustomCircularProgress />
        </Box>
      )}

      {hasMore && <Box ref={loadMoreTriggerRef} className={styles.loadMoreTrigger} />}

      <SettlementActionModal
        settlementId={actionModalSettlementId}
        opened={actionModalSettlementId !== null}
        setOpened={(value) => {
          if (!value) {
            setActionModalSettlementId(null);
          }
        }}
      />
    </Box>
  );
};
